import { useState } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import type { ChargingSessionType } from '@/api/schema';
import { ChargingStationTab } from '@/features/chargingActivity/components/Sidebar/ChargingStationTab';
import { SummaryTab } from '@/features/chargingActivity/components/Sidebar/SummaryTab';
import { useDisplayNames } from '@/hooks/useDisplayNames';
import { t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

const mapCredentialsSchema = z.object({
    app_id: z.string(),
    app_code: z.string(),
    apikey: z.string(),
});

const ChargingDetails = ({ state }: { state?: ChargingSessionType }) => {
    const { locale } = useIntl();
    const { currency } = useUserStore();
    const formatter = useDisplayNames();

    const [activeTab, setActiveTab] = useState<'summary' | 'chargepoint'>('summary');

    const credentials = mapCredentialsSchema.safeParse({
        app_id: import.meta.env.VITE_HERE_APP_ID,
        app_code: import.meta.env.VITE_HERE_APP_CODE,
        apikey: import.meta.env.VITE_HERE_APIKEY,
    });

    const lat = state?.chargingPoint?.location?.latitude;
    const lng = state?.chargingPoint?.location?.longitude;
    const linkToLiveMonitor = state?.chargingPoint?.fleetMonitorUrl;

    const location =
        lat && lng
            ? {
                  lat,
                  lng,
                  zoomLevel: 13,
              }
            : undefined;

    return (
        <div className='display-flex flex-column'>
            <ul className='nav nav-pills nav-justified margin-bottom-10'>
                <li
                    data-test-id='summary'
                    className={activeTab === 'summary' ? 'active' : undefined}
                    onClick={() => setActiveTab('summary')}
                >
                    <span>{t('chargingSessions.summary')}</span>
                </li>
                <li
                    data-test-id='chargepoint'
                    className={activeTab === 'chargepoint' ? 'active' : undefined}
                    onClick={() => setActiveTab('chargepoint')}
                >
                    <span>{t('chargingSessions.chargingPoint')}</span>
                </li>
            </ul>

            {activeTab === 'summary' && <SummaryTab state={state} locale={locale} currency={currency} />}

            {activeTab === 'chargepoint' && (
                <ChargingStationTab
                    state={state}
                    formatter={formatter}
                    credentials={credentials}
                    location={location}
                    linkToLiveMonitor={linkToLiveMonitor}
                />
            )}
        </div>
    );
};

export default ChargingDetails;
